import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useTools } from '../../contextApi/toolsContext';
import { TOOL_ITEMS } from '../navs/navConfig';
import { copyText } from '../clipboard';
import FloatingWindow, { clampRect } from './floatingWindow';
import MobileToolPage from './mobileToolPage';
import CalendarTool from './calendarTool';
import CalculatorTool from './calculatorTool';
import { press, keyAction, pasteKeys, resultText } from './calcEngine';
import { Z_BASE } from './toolStyles';

// The floating tools' host: mounted once in App.js for a signed-in user, it owns the shortcut keys (F5 calendar,
// F6 calculator), routes the keyboard to the calculator while it owns it, and draws whatever is open - as windows on a desktop,
// as a page over the screen on a phone.
//
// The tools' state is in ToolsProvider (contextApi/toolsContext.js); the screens underneath are never touched.

const CONTENT = {
  calendar:   { Content: CalendarTool,   min: { w: 300, h: 400 }, start: { w: 340, h: 680 } },
  calculator: { Content: CalculatorTool, min: { w: 250, h: 380 }, start: { w: 290, h: 470 } },
};
const MARGIN = 24;

// Where a tool opens the first time (or after a reset): the calendar at the right edge, the calculator just to its
// left, so opening both does not stack one on the other.
function defaultRect(name, vw, vh) {
  const { start } = CONTENT[name];
  const w = Math.min(start.w, vw - 2 * MARGIN);
  const h = Math.min(start.h, vh - 100);
  const calendarW = Math.min(CONTENT.calendar.start.w, vw - 2 * MARGIN);
  const x = name === 'calendar' ? vw - w - MARGIN : Math.max(MARGIN, vw - calendarW - MARGIN - 12 - w);
  return { x, y: 76, w, h };
}

// Public pages (a customer opening a link page) and the sign-in screen are not the app: F5 must still reload them.
const NOT_THE_APP = /^\/(p|l|logIn)(\/|$)/;

export default function FloatingTools() {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    open, toggle, closeTool, closeAll, raise, setMobile, rects, setRect, commitRects, resetRect,
    calc, setCalc, setCalcPanel, kbOwner, setKbOwner,
  } = useTools();
  const enabled = !NOT_THE_APP.test(location.pathname);

  useEffect(() => { setMobile(isMobile); }, [isMobile, setMobile]);

  // What the key handlers need, read at the moment of the key - so they are bound once, not on every keystroke in the calculator.
  const live = useRef({});
  live.current = { owner: kbOwner, calcOpen: open.includes('calculator'), calc };

  useEffect(() => {
    if (!enabled) return undefined;

    // F5 / F6, then (while the calculator owns the keyboard) everything the calculator has a key for. Capture phase, so it
    // works from inside a text field, and so a key the calculator takes never ALSO reaches the field underneath.
    const onKey = (e) => {
      if ((e.key === 'F5' || e.key === 'F6') && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();                               // F5 would reload the page and F6 would jump to the address bar
        if (e.repeat) return;                             // a held key must not flicker the window open and shut
        toggle(e.key === 'F5' ? 'calendar' : 'calculator');
        return;
      }
      const { owner, calcOpen, calc: current } = live.current;
      if (owner !== 'calculator' || !calcOpen || e.isComposing) return;
      const action = keyAction(e);
      if (!action) return;
      // Ctrl+C copies the display - unless the person has text selected somewhere, which is then what they want copied
      if (action.ui === 'copy' && window.getSelection && String(window.getSelection()).length > 0) return;
      e.preventDefault();
      e.stopPropagation();
      if (action.press) setCalc((s) => press(s, action.press));
      else if (action.ui === 'history') { if (!e.repeat) setCalcPanel((v) => !v); }
      else if (action.ui === 'clearHistory') setCalc((s) => ({ ...s, history: [] }));
      else if (action.ui === 'copy') {
        const text = resultText(current);
        if (text) copyText(text).then((ok) => { if (ok) window.dispatchEvent(new Event('xms-tools-copied')); });
      }
    };

    // Ctrl+V: the paste event, which needs no clipboard permission. Whatever is pasted while the calculator owns the keyboard is
    // the calculator's - a number or a sum is typed in, anything else is dropped rather than landing in a field underneath.
    const onPaste = (e) => {
      const { owner, calcOpen } = live.current;
      if (owner !== 'calculator' || !calcOpen) return;
      e.preventDefault();
      e.stopPropagation();
      const keys = pasteKeys(e.clipboardData && e.clipboardData.getData('text'));
      if (keys) setCalc((s) => keys.reduce(press, s));
    };

    // Who has the keyboard. The calculator keeps it while the person works in it, and lets go the moment they click or tab to
    // anything outside the tool windows (another section, a form field) - that is "the focus went elsewhere".
    const toolOf = (node) => (node && node.closest ? node.closest('[data-xms-tool]') : null);
    const onPointerDown = (e) => {
      const tool = toolOf(e.target);
      setKbOwner(tool && tool.dataset.xmsTool === 'calculator' ? 'calculator' : null);
    };
    const onFocusIn = (e) => {
      // the temporary textarea tools/clipboard.js copies through (when navigator.clipboard is missing, e.g. on a LAN http:// address)
      // takes the focus for a moment: that is a copy, not the person going elsewhere
      if (e.target && e.target.closest && e.target.closest('[data-clipboard-helper]')) return;
      const tool = toolOf(e.target);
      if (tool) setKbOwner(tool.dataset.xmsTool === 'calculator' ? 'calculator' : null);
      else if (live.current.owner) setKbOwner(null);
    };

    window.addEventListener('keydown', onKey, true);
    window.addEventListener('paste', onPaste, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('focusin', onFocusIn, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('paste', onPaste, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('focusin', onFocusIn, true);
    };
  }, [enabled, toggle, setCalc, setCalcPanel, setKbOwner]);

  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── phone: the page is one step in the browser's history, so Back closes it instead of leaving the screen ──
  const mobileOpen = enabled && isMobile && open.length > 0;
  const markerRef = useRef(false);
  useEffect(() => {
    if (mobileOpen && !markerRef.current) {
      window.history.pushState({ ...(window.history.state || {}), xmsTool: true }, '');
      markerRef.current = true;
    } else if (!mobileOpen && markerRef.current) {
      markerRef.current = false;
      if (window.history.state && window.history.state.xmsTool) window.history.back();   // closed with its own button: take our step back out
    }
  }, [mobileOpen]);
  useEffect(() => {
    const onPop = () => { if (markerRef.current) { markerRef.current = false; closeAll(); } };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeAll]);
  // The page covers the screen, so what is behind it must not scroll under a thumb. Locked on <html>, not <body>: the phone menu's
  // drawer sets and then RESTORES body's overflow when it finishes closing, which would undo the lock a moment after the page opens.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => { root.style.overflow = before; };
  }, [mobileOpen]);

  if (!enabled || open.length === 0) return null;
  const shown = isMobile ? open.slice(-1) : open;

  return createPortal(shown.map((name) => {
    const item = TOOL_ITEMS.find((x) => x.key === name);
    const { Content, min } = CONTENT[name];
    const title = t(item.labelKey);
    const onClose = () => closeTool(name);
    const escapeCloses = name !== 'calculator';             // Esc is the calculator's C (clear all), as in Windows

    if (isMobile) {
      return (
        <MobileToolPage key={name} name={name} title={title} icon={item.icon} onClose={onClose} escapeCloses={escapeCloses}>
          {(size) => <Content size={size} mobile onClose={onClose} />}
        </MobileToolPage>
      );
    }
    const rect = clampRect(rects[name] || defaultRect(name, vp.w, vp.h), vp.w, vp.h, min.w, min.h);
    return (
      <FloatingWindow key={name} name={name} title={title} icon={item.icon} shortcut={item.shortcut}
        rect={rect} minW={min.w} minH={min.h} zIndex={Z_BASE + open.indexOf(name)} escapeCloses={escapeCloses}
        onRect={(r) => setRect(name, r)} onCommit={commitRects} onReset={() => resetRect(name)}
        onRaise={() => raise(name)} onClose={onClose}>
        {(size) => <Content size={size} mobile={false} onClose={onClose} />}
      </FloatingWindow>
    );
  }), document.body);
}
